import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import MainScreenButton from '../../../../../components/forms/mainScreenButton';
import CurrentClinicianButton from '../../../forms/clinicianForm/currentClinician/currentClinicianButton';
import FirstClinicianButton from '../../../forms/clinicianForm/currentClinician/firstClinicianButton';
import SeniorReviewButton from '../../../forms/clinicianForm/seniorReview/seniorReviewButton';
import HandoverButton from '../../../forms/clinicianForm/handover/handoverButton';
import TriageButton from '../../../forms/triage/traigeButton';
import {ASSESSMENT_BUTTON_COLOURS, getLastAtoEAssessment} from '../../../forms/formsUtil';
import {getPatientFlagValue} from '../../util/patientFlagsUtil';
import ClerkingButton from '../../../forms/clerkingED/clerkingButton';
import {additionalData, setLoading} from '../../../../../store/reducers/additionalData';
import {hasOneOfPermission, hasPermission, Permissions} from '../../../../../store/reducers/user';
import {getConfig} from '../../../../../util/configUtil';
import Typography from '@mui/material/Typography';
import {
    boardRoundButtonColor,
    clerkingButtonColor,
    currentClinicianRender,
    formButtonColor,
    getClinicalConclusionBackground,
    getLastScreeningTool,
    getLastUrinalysis,
    getMessageToGpButtonBackground,
    getPSCBtnColor,
    getSeeAndTreatButtonBackground,
    getSepsisButtonBackground,
    intendentManagementButtonColor,
    intendentManagementRender,
    lastUpdateNutrition,
    oxygenRender,
    renderCSMLabel,
    renderDeliriumText,
    renderDependencyText,
    renderEmed3Text,
    renderPrescriptionText,
    renderReferralsText,
    renderRockwoodText,
    renderSeeAndTreatText,
    savedReferrals,
    smokerRender,
} from './buttonsUtils';
import Formatter from '../../../../../util/formatter';
import {PATIENT_AGE_TYPE} from '../../../../../util/patientContextUtil';
import {openPatientForm} from '../../../../../store/reducers/patientContext';
import {addWorkflowStep, nextWorkflowStep} from '../../../../../store/reducers/workflow';
import {WORKFLOW_OPTIONS} from '../../../../../util/workflowUtil';
import moment from 'moment/moment';
import {agyleAssessmentList} from '../../../../../store/reducers/dicts';

function createButton(id, button, enabled) {
    return {id, button, enabled};
}

const PatientContextButtonRender = ({type, patient, patientAgeType, disableSave, isAssessment = false}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Selectors
    const assessmentList = useSelector(agyleAssessmentList);
    const patientAddData = useSelector(additionalData);

    const [selectedButton, setSelectedButton] = useState(null);
    const isED = patient.wardId === 'ED';

    // Permissions
    const canViewReferrals = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_REFERRALS, Permissions.AGYLE_CAN_MANAGE_REFERRALS]));
    const canSetClinician = dispatch(hasPermission(Permissions.AGYLE_CAN_SET_CLINICIAN));
    const canViewNutrition = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_NUTRITION, Permissions.AGYLE_CAN_MANAGE_NUTRTION]));
    const canViewPaedSafeguarding = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_PAEDIATRIC_SAFEGUARDING, Permissions.AGYLE_CAN_MANAGE_PAEDIATRIC_SAFEGUARDING]));
    const canViewSepsis = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_MANAGE_SEPSIS, Permissions.AGYLE_CAN_VIEW_SEPSIS]));
    const canViewUrinalysis = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_URINALYSIS, Permissions.AGYLE_CAN_ADD_URINALYSIS, Permissions.AGYLE_CAN_EDIT_URINALYSIS]));
    const canViewVte = dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_LOWER_LIMB_VTE));
    const canEditVte = dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_LOWER_LIMB_VTE));

    // Configs
    const diagnosisRequiredDialog = getConfig('diagnosisRequiredDialog', false);
    const frailtyAgyleMiniForm = getConfig('frailtyAgyleMiniForm', false);
    const nonContactObsAvailable = getConfig('nonContactObsAvailable', false);
    const isUHDMap = getConfig('modules.map.customMap', false);
    const prescriptionFormEnabled = getConfig('prescriptionFormEnabled', false);
    const nursingAdmissionAvailable = getConfig('nursingAdmissionAvailable', false);
    const fitnessForWorkEnabled = getConfig('fitnessForWorkEnabled', false);
    const oldBedRequest = getConfig('modules.forms.bedBooking.enableBedRequestForm', false);
    const frailtySDECFormEnabled = useMemo(() => _.find(assessmentList, { code: 'frailtySDECForm', active: true }) && getConfig('frailtySDECForm', false), [assessmentList]);
    const abbeyPainScaleFormEnabled = useMemo(() => _.find(assessmentList, { code: 'abbeyPainScaleForm', active: true }) && getConfig('abbeyPainScaleForm', false), [assessmentList]);

    const handleOpenReferrals = () => {
        if (diagnosisRequiredDialog) {
            // If diagnosis is empty, display warning
            if (_.isEmpty(patient?.diagnosis?.primary) && !patient?.metadata?.diagnosisNotRequired?.acknowledge) {
                dispatch(addWorkflowStep([WORKFLOW_OPTIONS.OPEN_EMPTY_DIAGNOSIS_MODAL]));
                dispatch(nextWorkflowStep());
            } else {
                dispatch(openPatientForm('specReferrals'));
            }
        } else {
            dispatch(openPatientForm('specReferrals'));
        }
    };

    const filledAdditionalData = (assessmentPath) => {
        try {
            const assessment = _.find(assessmentList, {code: assessmentPath});
            const {path} = assessment.metadata;
            const data = _.get(patientAddData, path.filled);
            if (data) {
                let filteredData = _.isArray(data) ? data : [data];
                filteredData = _.filter(filteredData, f => {
                    if (f.canceled) return false;

                    const activityId = f?.attendanceId;
                    const date = _.get(f, path.date);

                    if (activityId) {
                        return patient?.admission?.__id === activityId;
                    }
                    if (date) {
                        const startDate = moment(patient?.admission?.startDate);
                        const isActive = _.isNil(patient?.admission?.endDate);
                        const endDate = isActive
                            ? moment()
                            : moment(patient?.admission?.startDate);

                        return moment(date).isBetween(startDate, endDate, null, '[]');
                    }
                    return null;
                });
                return !_.isEmpty(filteredData);
            }
            return false;
        } catch (e) {
            console.error(e);
            return false;
        }
    };

    // Button configurations
    const buttons = useMemo(() => [
        createButton(
            'bedRequest',
            <MainScreenButton
                label={t('Bed Request')}
                onOpen={() => dispatch(openPatientForm('bedRequest'))}
                colorType="notMandatory"
                filled={!!patient?.studies?.bedRequest}
                value={patient?.studies?.bedRequest}
                userPath="lastUser"
                extraLine={() => renderCSMLabel(patient)}
                customColor
                btnBackground={() => renderCSMLabel(patient, true)}
                isAssessment={isAssessment}
            />,
            oldBedRequest,
        ),
        createButton(
            'csm',
            <MainScreenButton
                label={t('CSM Contact Time')}
                onOpen={() => dispatch(openPatientForm('csm'))}
                colorType="notMandatory"
                filled={!!patient?.csmContactedTime?.by}
                value={patient?.csmContactedTime}
                userPath="by"
                extraLine={() => renderCSMLabel(patient)}
                customColor
                btnBackground={() => renderCSMLabel(patient, true)}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'clinicalConclusion',
            <MainScreenButton
                label={t('Clinical Conclusion')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('clinicalConclusion'))}
                filled={!!patient?.clinicalConclusion?.who}
                value={patient?.clinicalConclusion}
                extraLine={() => Formatter.getClinicalConclusionStatusText(patient?.clinicalConclusion?.status)}
                customColor
                btnBackground={() => getClinicalConclusionBackground(patient)}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'msgToGp',
            <MainScreenButton
                label={t(isED ? 'Message to GP / Plan & Advice Given' : 'Message to GP')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('msgToGp'))}
                filled={!!patient?.studies?.clinicalCourse}
                value={patient?.studies?.clinicalCourse}
                customColor
                btnBackground={() => getMessageToGpButtonBackground(patient)}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'currentClinician',
            <CurrentClinicianButton
                patient={patient}
                disableSave={disableSave}
                value={patient?.currentClinician || null}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'firstClinician',
            <FirstClinicianButton
                patient={patient}
                disableSave={disableSave}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'currentNurse',
            <CurrentClinicianButton
                patient={patient}
                disableSave={disableSave}
                value={patient?.studies?.edau?.currentNurse || null}
                nurse
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'rapidAssessment',
            <MainScreenButton
                label={t('Rapid Assessment')}
                disabled={!canSetClinician}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('rapidAssessment'))}
                filled={!!patient?.seen?.who}
                value={patient?.seen}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'seniorReview',
            <SeniorReviewButton
                patient={patient}
                disableSave={disableSave}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'handover',
            <HandoverButton
                patient={patient}
                disableSave={disableSave}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'nurseHandover',
            <HandoverButton
                patient={patient}
                disableSave={disableSave}
                nurseHandover
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'dependency',
            <MainScreenButton
                label={t('Dependency')}
                filled={!!patient?.triage?.dependency}
                value={patient?.triage?.dependency}
                onOpen={() => dispatch(openPatientForm('dependency'))}
                spacialRender={() => renderDependencyText(patient)}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'triage',
            <TriageButton
                color={ASSESSMENT_BUTTON_COLOURS.mandatoryEmpty}
                size="large"
                majorTrauma={getPatientFlagValue(patient, 'majorTrauma')}
                value={patient?.triage || null}
                filled={patient?.triage?.who || null}
                datePath="when"
                patient={patient}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'nursingChecklist',
            <MainScreenButton
                label={t('Nursing Checklist Questions')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('nursingChecklist'))}
                filled={!!patient?.studies?.edau?.nurseForm?.who}
                value={patient?.studies?.edau?.nurseForm}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'medicalChecklist',
            <MainScreenButton
                label={t('Medical Checklist Questions')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('medicalChecklist'))}
                filled={!!patient?.studies?.edau?.medicationForm?.who}
                value={patient?.studies?.edau?.medicationForm}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'seeAndTreat',
            <MainScreenButton
                label={t('See and Treat')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('snt'))}
                customColor
                btnBackground={() => getSeeAndTreatButtonBackground(patient)}
                isDraft={patient?.studies?.seeAndTreat?.status === 'draft'}
                filled={!!patient?.studies?.seeAndTreat}
                value={patient?.studies?.seeAndTreat}
                spacialRender={() => renderSeeAndTreatText(patient)}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'clerkingED',
            <ClerkingButton
                name="Clerking"
                size="large"
                onClick={() => {
                    dispatch(setLoading(true));
                    setTimeout(() => {
                        dispatch(openPatientForm('clerkingED'));
                    }, 1);
                }}
                color={
                    patient?.clerking?.when &&
                    patient?.clerking?.status !== 'timestamp'
                        ? ASSESSMENT_BUTTON_COLOURS.mandatorySet
                        : ASSESSMENT_BUTTON_COLOURS.mandatoryEmpty
                }
                value={patient?.clerking}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'referrals',
            <MainScreenButton
                label={t('Speciality Referrals')}
                colorType="notMandatory"
                onOpen={handleOpenReferrals}
                filled={savedReferrals(patient) > 0}
                customColor
                btnBackground={() => renderReferralsText(patient, false, true)}
                disabled={!canViewReferrals}
                spacialRender={() => {
                    return savedReferrals(patient) > 0 ?
                        <div style={{display: 'flex', flexDirection: 'column'}}>
                            <Typography variant="subtitle1" fontWeight="bolder">
                                <div style={{paddingLeft: 8}}>
                                    {renderReferralsText(patient, true)}
                                </div>
                                <div>{renderReferralsText(patient)}</div>
                            </Typography>
                        </div> : null;
                }}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'boardRound',
            <MainScreenButton
                label={t('Board Round')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('boardRound'))}
                btnBackground={() => boardRoundButtonColor(patient)}
                customColor
                filled={!!patient?.boardRound}
                value={patient?.boardRound?.list.length > 0 ? patient?.boardRound.list[0] : null}
                userPath="by"
                datePath="date"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'currentClinicianIP',
            <MainScreenButton
                label={t('Current Clinician')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('currentClinicianIP'))}
                filled={!!patient?.currentClinicianIP}
                spacialRender={() => currentClinicianRender(patient)}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'currentNurseIP',
            <CurrentClinicianButton
                patient={patient}
                disableSave={disableSave}
                value={patient?.currentNurse || null}
                nurseIP
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'oxygen',
            <MainScreenButton
                label={t('Oxygen')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('oxygen'))}
                spacialRender={() => oxygenRender(patient)}
                filled={!!patient?.oxygen}
                value={patient?.oxygen}
                userPath="who"
                datePath="date"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'clerkingIP',
            <MainScreenButton
                label={t('Clerking')}
                colorType="notMandatory"
                btnBackground={() => clerkingButtonColor(patient)}
                customColor
                onOpen={() => dispatch(openPatientForm('clerkingIP'))}
                filled={!!patient?.clerkingIP}
                value={patient?.clerkingIP}
                userPath="by"
                datePath="createDate"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'auditC',
            <MainScreenButton
                label={t('Audit C')}
                colorType="notMandatory"
                btnBackground={() => formButtonColor(patient?.auditC)}
                onOpen={() => dispatch(openPatientForm('auditC'))}
                customColor
                filled={!!patient?.auditC}
                value={patient?.auditC}
                userPath="who"
                datePath="date"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'intendentManagement',
            <MainScreenButton
                label={t('Intendent Management')}
                colorType="mandatory"
                btnBackground={() => intendentManagementButtonColor(patient)}
                customColor
                onOpen={() => dispatch(openPatientForm('intendentManagement'))}
                spacialRender={() => intendentManagementRender(patient)}
                filled={!!patient?.intendentManagement}
                value={patient?.intendentManagement}
                userPath="who"
                datePath="date"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'injury',
            <MainScreenButton
                label={t(patientAgeType !== PATIENT_AGE_TYPE.PAEDS ? 'Injury' : 'CWILTED')}
                colorType="notMandatory"
                openFormPath="injury"
                filled={!_.isEmpty(_.filter(patient?.studies?.injury?.list, (form) => !form?.canceled))}
                value={patient?.studies?.injury}
                disabled={!(dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_EDIT_CEILTWD)) || (dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_CEILTWD)) && patient.studies?.injury?.by))}
                userPath="by"
                datePath="date"
                isMultiple
                multiplePath="studies.injury.list"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'delirium',
            <MainScreenButton
                label={renderDeliriumText(patient, true)}
                colorType="notMandatory"
                openFormPath="delirium"
                filled={!_.isEmpty(_.filter(patient?.studies?.delirium?.list, (form) => !form?.canceled))}
                spacialRender={() => renderDeliriumText(patient)}
                isMultiple
                multiplePath="studies.delirium.list"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'clinicalFrailtyScore',
            <MainScreenButton
                label={renderRockwoodText(patient, true)}
                colorType="notMandatory"
                openFormPath="clinicalFrailtyScore"
                filled={!_.isEmpty(_.filter(patient.studies?.rockwood?.list, (form) => !form?.canceled))}
                spacialRender={() => renderRockwoodText(patient)}
                isMultiple
                multiplePath="studies.rockwood.list"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'frailtyHistory',
            <MainScreenButton
                label={t('Frailty - Social History')}
                colorType={'notMandatory'}
                onOpen={() => dispatch(openPatientForm('frailtyHistory'))}
                filled={!!patient?.studies?.frailtyForm}
                value={patient?.studies?.frailtyForm}
                userPath={patient?.studies?.frailtyForm?.lastUpdateBy ? 'lastUpdateBy' : 'by'}
                datePath={patient?.studies?.frailtyForm?.lastUpdateDate ? 'lastUpdateDate' : 'timeStamp'}
                disabled={!dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_FRAILITY]))}
                isAssessment={isAssessment}
            />,
            frailtyAgyleMiniForm,
        ),
        createButton(
            'household',
            <MainScreenButton
                label={t('Household')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('household'))}
                filled={!_.isEmpty(patientAddData?.other?.household)}
                value={_.last(patientAddData?.other?.household)}
                disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_HOUSEHOLD))}
                userPath="by"
                datePath="date"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'aeAssessment',
            <MainScreenButton
                label={t('A to E Assessment')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('aeAssessment'))}
                filled={getLastAtoEAssessment(patient, true)}
                value={getLastAtoEAssessment(patient)}
                userPath="by"
                datePath="date"
                isAssessment={isAssessment}
            />,
            nonContactObsAvailable,
        ),
        createButton(
            'nutritionScreen',
            <MainScreenButton
                label={t(patientAgeType !== PATIENT_AGE_TYPE.PAEDS ? 'Weight / Height / BMI' : 'Weight / Height')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('nutritionScreen'))}
                filled={filledAdditionalData('nutritionScreen')}
                spacialRender={() => filledAdditionalData('nutritionScreen') && lastUpdateNutrition(patientAddData, patientAgeType === PATIENT_AGE_TYPE.PAEDS)}
                disabled={!canViewNutrition}
                openFormPath="nutritionScreen"
                isMultiple
                multiplePath="other.nutritionScreen"
                isPatientAddData
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'paedSafeguarding',
            <MainScreenButton
                label={t('Paediatric Safeguarding')}
                colorType="notMandatory"
                openFormPath="paedSafeguarding"
                filled={!_.isEmpty(patient?.studies?.paedSafeguarding?.list)}
                value={patient?.studies?.paedSafeguarding}
                disabled={!canViewPaedSafeguarding}
                userPath="by"
                datePath="date"
                isMultiple
                multiplePath="studies.paedSafeguarding.list"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'safetyChecklist',
            <MainScreenButton
                label={t(isUHDMap ? 'ADMISSION and SAFETY CHECKLIST' : 'Patient Safety Checklist')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('safetyChecklist'))}
                customColor
                patient={patient}
                btnBackground={() => getPSCBtnColor(patient)}
                filled={!!patient?.studies?.safetyChecklist}
                value={patient?.studies?.safetyChecklist}
                userPath={patient?.studies?.safetyChecklist?.lastUpdateBy ? 'lastUpdateBy' : 'by'}
                datePath={patient?.studies?.safetyChecklist?.lastUpdateDate ? 'lastUpdateDate' : 'date'}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'prescription',
            <MainScreenButton
                label={t('Prescription Form')}
                colorType="notMandatory"
                openFormPath="prescription"
                filled={!_.isEmpty(_.filter(patient.studies?.prescription?.list, (form) => !form?.canceled))}
                spacialRender={() => renderPrescriptionText(patient)}
                isMultiple
                multiplePath="studies.prescription.list"
                isAssessment={isAssessment}
            />,
            prescriptionFormEnabled,
        ),
        createButton(
            'propertyList',
            <MainScreenButton
                label={t('Property List')}
                colorType={'notMandatory'}
                onOpen={() => dispatch(openPatientForm('propertyList'))}
                filled={!!patient?.studies?.nursingAdmission}
                value={patient?.studies?.nursingAdmission}
                userPath={patient?.studies?.nursingAdmission?.lastUpdateBy ? 'lastUpdateBy' : 'by'}
                datePath={patient?.studies?.nursingAdmission?.lastUpdateDate ? 'lastUpdateDate' : 'date'}
                isAssessment={isAssessment}
            />,
            nursingAdmissionAvailable,
        ),
        createButton(
            'propertyList',
            <MainScreenButton
                label={t('Property List')}
                colorType={'notMandatory'}
                onOpen={() => dispatch(openPatientForm('propertyList'))}
                filled={!!patient?.studies?.nursingAdmission}
                value={patient?.studies?.nursingAdmission}
                userPath={patient?.studies?.nursingAdmission?.lastUpdateBy ? 'lastUpdateBy' : 'by'}
                datePath={patient?.studies?.nursingAdmission?.lastUpdateDate ? 'lastUpdateDate' : 'date'}
                isAssessment={isAssessment}
            />,
            nursingAdmissionAvailable,
        ),
        createButton(
            'sepsisTool',
            <MainScreenButton
                label={t('Sepsis Screening Tool')}
                colorType="notMandatory"
                openFormPath="sepsisTool"
                filled={!_.isEmpty(getLastScreeningTool(patientAddData, patient))}
                customColor
                btnBackground={() => getSepsisButtonBackground(patientAddData, patient)}
                disabled={!canViewSepsis}
                customData={getLastScreeningTool(patientAddData, patient)}
                isMultiple
                multiplePath="other.sepsisTool"
                isPatientAddData
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'smoking',
            <MainScreenButton
                label={t('Smoking Status')}
                colorType="notMandatory"
                spacialRender={() => smokerRender(patient)}
                onOpen={() => dispatch(openPatientForm('smoking'))}
                filled={!!patient?.smoking}
                value={patient?.smoking}
                userPath="who"
                datePath="date"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'urinalysis',
            <MainScreenButton
                label={t('Urinalysis Assessment')}
                colorType={'notMandatory'}
                onOpen={() => dispatch(openPatientForm('urinalysis'))}
                filled={!_.isEmpty(getLastUrinalysis(patientAddData, patient))}
                customData={getLastUrinalysis(patientAddData, patient)}
                disabled={!canViewUrinalysis}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'vod',
            <MainScreenButton
                label={t('Verification of Death')}
                colorType={'notMandatory'}
                onOpen={() => dispatch(openPatientForm('vod'))}
                filled={!!patient?.studies?.verificationOfDeath?.clinicianName}
                value={patient?.studies?.verificationOfDeath}
                userPath={patient?.studies?.verificationOfDeath?.lastUpdateBy ? 'lastUpdateBy' : 'by'}
                datePath={patient?.studies?.verificationOfDeath?.lastUpdateDate ? 'lastUpdateDate' : 'when'}
                disabled={!dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VERIFICATIONOFDEATH]))}
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'fitnessForWork',
            <MainScreenButton
                label={renderEmed3Text(patient, true)}
                colorType="notMandatory"
                openFormPath="fitnessForWork"
                filled={!_.isEmpty(_.filter(patient.studies?.fitnessForWork?.list, (form) => !form?.canceled))}
                disabled={!dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_MANAGE_EMED3]))}
                userPath="by"
                datePath="date"
                value={patient?.studies?.fitnessForWork}
                spacialRender={() => renderEmed3Text(patient)}
                isMultiple
                multiplePath="studies.fitnessForWork.list"
                isAssessment={isAssessment}
            />,
            fitnessForWorkEnabled,
        ),
        createButton(
            'vte',
            <MainScreenButton
                label={t('VTE')}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('vte'))}
                filled={patient?.vte?.length > 0}
                value={patient?.vte?.length > 0 ? patient?.vte[patient?.vte?.length - 1] : null}
                disabled={!(canEditVte || (canViewVte && patient?.vte?.length > 0))}
                userPath="createdWho"
                datePath="createdWhen"
                isAssessment={isAssessment}
            />,
            true,
        ),
        createButton(
            'frailtySDECForm',
            <MainScreenButton
                label={t("Frailty SDEC Comprehensive Geriatric Assessment")}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('frailtySDECForm'))}
                filled={!!patient?.studies?.frailtySDECForm}
                value={patient?.studies?.frailtySDECForm}
                userPath={patient?.studies?.frailtySDECForm?.lastUpdateBy ? "lastUpdateBy" : "by"}
                datePath={patient?.studies?.frailtySDECForm?.lastUpdateDate ? "lastUpdateDate" : "timeStamp"}
                isAssessment={isAssessment}
            />,
            frailtySDECFormEnabled
        ),
        createButton(
            'abbeyPainScaleForm',
            <MainScreenButton
                label={t("Abbey Pain Score")}
                colorType="notMandatory"
                onOpen={() => dispatch(openPatientForm('abbeyPainScaleForm'))}
                filled={!!patient?.studies?.abbeyPainScoreForm}
                value={patient?.studies?.abbeyPainScoreForm}
                userPath={patient?.studies?.abbeyPainScoreForm?.lastUpdateBy ? "lastUpdateBy" : "by"}
                datePath={patient?.studies?.abbeyPainScoreForm?.lastUpdateDate ? "lastUpdateDate" : "timeStamp"}
                isAssessment={isAssessment}
            />,
            abbeyPainScaleFormEnabled
        ),
    ], [patient, disableSave, patientAgeType, patientAddData, abbeyPainScaleFormEnabled, frailtySDECFormEnabled]);
    // ''

    useEffect(() => {
        const find = _.find(buttons, {id: type, enabled: true});
        setSelectedButton(find || null);
    }, [type, patient, patientAddData]);

    if (!selectedButton) return null;

    return (
        <Fragment key={selectedButton?.id || Math.random()}>
            {React.cloneElement(selectedButton.button)}
        </Fragment>
    );
};

export default PatientContextButtonRender;